
import { useEffect, type RefObject } from 'react'

/**
 * What native `<details>` does not give a popover: Escape closes it and hands
 * focus back to the control, a click anywhere else closes it, and opening one
 * closes any other that is open.
 *
 * Shared by the rail's Popover component and by the two menus that keep their
 * own ref for other reasons (the feedback form loads on open, the export menu
 * closes itself after an export).
 */
export function useDetailsPopover(
  ref: RefObject<HTMLDetailsElement | null>,
  options: { onOpenChange?: (open: boolean) => void } = {},
) {
  const { onOpenChange } = options

  useEffect(() => {
    const details = ref.current
    if (!details) return

    const close = () => details.open && details.removeAttribute('open')

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || !details?.open) return
      event.stopPropagation()
      close()
      details.querySelector('summary')?.focus()
    }
    function onPointerDown(event: PointerEvent) {
      if (!details?.open) return
      if (!details.contains(event.target as Node)) close()
    }
    function onToggle() {
      onOpenChange?.(Boolean(details?.open))
      if (!details?.open) return
      for (const other of document.querySelectorAll<HTMLDetailsElement>('details[open]')) {
        if (other !== details) other.removeAttribute('open')
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown, true)
    details.addEventListener('toggle', onToggle)
    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown, true)
      details.removeEventListener('toggle', onToggle)
    }
  }, [ref, onOpenChange])
}
